/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { GetSmsTemplateDto } from '../models/GetSmsTemplateDto';
import type { MedicalRecordsViewingLinkResponseDto } from '../models/MedicalRecordsViewingLinkResponseDto';
import type { Protocol801ParticipationUpdateDto } from '../models/Protocol801ParticipationUpdateDto';
import type { ProtocolInfoDto } from '../models/ProtocolInfoDto';
import type { ProtocolListDto } from '../models/ProtocolListDto';
import type { ReferralActivitiesDto } from '../models/ReferralActivitiesDto';
import type { ReferralDto } from '../models/ReferralDto';
import type { ReferralInfoDto } from '../models/ReferralInfoDto';
import type { ReferralInvestigatorDto } from '../models/ReferralInvestigatorDto';
import type { ReferralLockDto } from '../models/ReferralLockDto';
import type { ReferralNoteChangeDto } from '../models/ReferralNoteChangeDto';
import type { ReferralRecontactMarkDto } from '../models/ReferralRecontactMarkDto';
import type { ReferralRelocationDto } from '../models/ReferralRelocationDto';
import type { ReferralSearchParametersDto } from '../models/ReferralSearchParametersDto';
import type { ReferralSearchResultDto } from '../models/ReferralSearchResultDto';
import type { ReferralStatusChangeDto } from '../models/ReferralStatusChangeDto';
import type { ReferralUpdateDto } from '../models/ReferralUpdateDto';
import type { ReferralXlsxDto } from '../models/ReferralXlsxDto';
import type { RequestMedicalRecordsDto } from '../models/RequestMedicalRecordsDto';
import type { RequestMedicalRecordsResponseDto } from '../models/RequestMedicalRecordsResponseDto';
import type { SendAutomatedEmailToPatientDto } from '../models/SendAutomatedEmailToPatientDto';
import type { SendEmailToPatientDto } from '../models/SendEmailToPatientDto';
import type { HealthcareProviderDto } from '../models/HealthcareProviderDto';
import type { SponsorPatientIDDto } from '../models/SponsorPatientIDDto';
import type { UpdateHealthcareProviderDto } from '../models/UpdateHealthcareProviderDto';
import type { Visit601StatusDto } from '../models/Visit601StatusDto';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class ReferralService {

    /**
     * Get referrals
     * @returns ReferralDto
     * @throws ApiError
     */
    public static referralControllerList(): CancelablePromise<Array<ReferralDto>> {
        return __request({
            method: 'GET',
            path: `/referral`,
        });
    }

    /**
     * Get referrals without screener data
     * @returns ReferralDto
     * @throws ApiError
     */
    public static referralControllerGetPatientDetailsList(): CancelablePromise<Array<ReferralDto>> {
        return __request({
            method: 'GET',
            path: `/referral/patientdetails`,
        });
    }

    /**
     * Run migration to add Show to investigators to all existing questions in a json
     * @returns string
     * @throws ApiError
     */
    public static referralControllerRunVisibleToInvestigatorsMigration(): CancelablePromise<string> {
        return __request({
            method: 'GET',
            path: `/referral/run-visible-to-investigators-migration`,
        });
    }

    /**
     * Search referrals
     * @param requestBody
     * @returns ReferralSearchResultDto
     * @throws ApiError
     */
    public static referralControllerSearch(
        requestBody: ReferralSearchParametersDto,
    ): CancelablePromise<ReferralSearchResultDto> {
        return __request({
            method: 'PUT',
            path: `/referral/search`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Search contact center list
     * @param requestBody
     * @returns ReferralSearchResultDto
     * @throws ApiError
     */
    public static referralControllerCclistSearch(
        requestBody: ReferralSearchParametersDto,
    ): CancelablePromise<ReferralSearchResultDto> {
        return __request({
            method: 'PUT',
            path: `/referral/search/cclist`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Search referrals by patient phone number
     * @param phoneNumber
     * @returns ReferralInfoDto
     * @throws ApiError
     */
    public static referralControllerSearchByPatientPhoneNumber(
        phoneNumber: string,
    ): CancelablePromise<ReferralInfoDto> {
        return __request({
            method: 'GET',
            path: `/referral/search/patientphone/${phoneNumber}`,
        });
    }

    /**
     * Get referral CSV
     * @param referralId
     * @param patientId
     * @returns string
     * @throws ApiError
     */
    public static referralControllerOfflineScreener(
        referralId: string,
        patientId?: string,
    ): CancelablePromise<string> {
        return __request({
            method: 'GET',
            path: `/referral/offlineScreener`,
            query: {
                'referralId': referralId,
                'patientId': patientId,
            },
        });
    }

    /**
     * Get external link to Offline Screener on NL
     * @param id
     * @returns string
     * @throws ApiError
     */
    public static referralControllerOfflineScreenerGenerateUrl(
        id: string,
    ): CancelablePromise<string> {
        return __request({
            method: 'GET',
            path: `/referral/offlineScreener/generate-url/${id}`,
        });
    }

    /**
     * Get referral
     * @param id
     * @returns ReferralDto
     * @throws ApiError
     */
    public static referralControllerGet(
        id: string,
    ): CancelablePromise<ReferralDto> {
        return __request({
            method: 'GET',
            path: `/referral/${id}`,
        });
    }

    /**
     * Get referral
     * @param id
     * @param source When `external`, dispatches to the external-referral read path and returns a PDF for an `ExternalReferralDetailsDto` (ReferralDto-compatible).
     * @returns binary
     * @throws ApiError
     */
    public static referralControllerGetPdfForCurrentUser(
        id: string,
        source?: 'external',
    ): CancelablePromise<Blob> {
        return __request({
            method: 'GET',
            path: `/referral/currentuser/${id}/pdf`,
            query: {
                'source': source,
            },
        });
    }

    /**
     * Get referral (regular or external) for current investigator
     * @param id
     * @param source When `external`, dispatches to the external-referral read path and returns an `ExternalReferralDetailsDto` (ReferralDto-compatible).
     * @returns ReferralDto
     * @throws ApiError
     */
    public static referralControllerGetForCurrentUser(
        id: string,
        source?: 'external',
    ): CancelablePromise<ReferralDto> {
        return __request({
            method: 'GET',
            path: `/referral/currentuser/${id}`,
            query: {
                'source': source,
            },
        });
    }

    /**
     * Get screener
     * @param id
     * @returns ReferralDto
     * @throws ApiError
     */
    public static referralControllerGetScreenerForCurrentUser(
        id: string,
    ): CancelablePromise<ReferralDto> {
        return __request({
            method: 'GET',
            path: `/referral/currentuserscreener/${id}`,
        });
    }

    /**
     * Get referral
     * @param id
     * @returns ReferralInfoDto
     * @throws ApiError
     */
    public static referralControllerGetSiteTimeZoneCurrentUser(
        id: string,
    ): CancelablePromise<ReferralInfoDto> {
        return __request({
            method: 'GET',
            path: `/referral/currentuser/siteTimeZone/${id}`,
        });
    }

    /**
     * Get referral
     * @param id
     * @returns ReferralInvestigatorDto
     * @throws ApiError
     */
    public static referralControllerGetForCurrentUserAdmin(
        id: string,
    ): CancelablePromise<ReferralInvestigatorDto> {
        return __request({
            method: 'GET',
            path: `/referral/currentuseradmin/${id}`,
        });
    }

    /**
     * Get referral
     * @param id
     * @returns ReferralInvestigatorDto
     * @throws ApiError
     */
    public static referralControllerGetScreenerForCurrentUserAdmin(
        id: string,
    ): CancelablePromise<ReferralInvestigatorDto> {
        return __request({
            method: 'GET',
            path: `/referral/currentscreeneruseradmin/${id}`,
        });
    }

    /**
     * Get referral activities
     * @param id
     * @param returnUserName
     * @param includeCcStatues
     * @param source When `external`, returns activity for an external referral (external_referral + referral_activity).
     * @returns ReferralActivitiesDto
     * @throws ApiError
     */
    public static referralControllerGetActivities(
        id: string,
        returnUserName?: boolean,
        includeCcStatues?: boolean,
        source?: 'external',
    ): CancelablePromise<ReferralActivitiesDto> {
        return __request({
            method: 'GET',
            path: `/referral/${id}/activities`,
            query: {
                'returnUserName': returnUserName,
                'includeCCStatues': includeCcStatues,
                'source': source,
            },
        });
    }

    /**
     * Get referral status history
     * @param id
     * @param source When `external`, returns the status history for an external referral (external_referral + referral_activity).
     * @param returnUserName
     * @returns ReferralActivitiesDto
     * @throws ApiError
     */
    public static referralControllerGetHistory(
        id: string,
        source?: 'external',
        returnUserName?: any,
    ): CancelablePromise<ReferralActivitiesDto> {
        return __request({
            method: 'GET',
            path: `/referral/${id}/history`,
            query: {
                'source': source,
                'returnUserName': returnUserName,
            },
        });
    }

    /**
     * Change referral status
     * @param id
     * @param requestBody
     * @returns ReferralDto
     * @throws ApiError
     */
    public static referralControllerChangeStatus(
        id: string,
        requestBody: ReferralStatusChangeDto,
    ): CancelablePromise<ReferralDto> {
        return __request({
            method: 'PATCH',
            path: `/referral/${id}/status`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Revert a consented or randomized referral status
     * @param id
     * @param requestBody
     * @returns ReferralDto
     * @throws ApiError
     */
    public static referralControllerRevertFinalStatus(
        id: string,
        requestBody: ReferralStatusChangeDto,
    ): CancelablePromise<ReferralDto> {
        return __request({
            method: 'PATCH',
            path: `/referral/${id}/status/revert`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Relocate referral
     * @param requestBody
     * @returns ReferralDto
     * @throws ApiError
     */
    public static referralControllerRelocateReferral(
        requestBody: ReferralRelocationDto,
    ): CancelablePromise<ReferralDto> {
        return __request({
            method: 'PATCH',
            path: `/referral/relocation`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Change referral status
     * @param id
     * @param requestBody
     * @returns ReferralDto
     * @throws ApiError
     */
    public static referralControllerChangeStatusAsCurrentUser(
        id: string,
        requestBody: ReferralStatusChangeDto,
    ): CancelablePromise<ReferralDto> {
        return __request({
            method: 'PATCH',
            path: `/referral/${id}/currentuser/status`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Change referral status
     * @param id
     * @param requestBody
     * @param source When `external`, updates the note stored on external_referral and writes a NOTE_CHANGE activity row.
     * @returns ReferralNoteChangeDto
     * @throws ApiError
     */
    public static referralControllerChangeNoteAsCurrentUser(
        id: string,
        requestBody: ReferralNoteChangeDto,
        source?: 'external',
    ): CancelablePromise<ReferralNoteChangeDto> {
        return __request({
            method: 'PATCH',
            path: `/referral/${id}/currentuser/note`,
            query: {
                'source': source,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Get referral excel file
     * @param requestBody
     * @returns string xlsx file in base64 format
     * @throws ApiError
     */
    public static referralControllerDownloadXlsx(
        requestBody: ReferralXlsxDto,
    ): CancelablePromise<string> {
        return __request({
            method: 'POST',
            path: `/referral/downloadXlsx`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * update referral
     * @param requestBody
     * @returns ReferralDto
     * @throws ApiError
     */
    public static referralControllerUpdateReferral(
        requestBody: ReferralUpdateDto,
    ): CancelablePromise<ReferralDto> {
        return __request({
            method: 'PATCH',
            path: `/referral/update`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Send automated email to patient
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static referralControllerSendAutomatedEmailToPatient(
        requestBody: SendAutomatedEmailToPatientDto,
    ): CancelablePromise<any> {
        return __request({
            method: 'POST',
            path: `/referral/sendAutomatedEmailToPatient`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Send email to patient
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static referralControllerSendEmailToPatient(
        requestBody: SendEmailToPatientDto,
    ): CancelablePromise<any> {
        return __request({
            method: 'POST',
            path: `/referral/sendEmailToPatient`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Get email template
     * @param referralId
     * @param templateKey
     * @returns any
     * @throws ApiError
     */
    public static referralControllerGetEmailTemplate(
        referralId: string,
        templateKey: 'INVITE_TO_RESCHEDULE' | 'SCHEDULED_MEETING_CONFIRMATION' | 'INVALID_PHONE_NUMBER' | 'NEWSLETTER_INVITATION' | 'SITE_EMPLOYEE_INVITATION' | 'RESET_PASSWORD' | 'NEW_APPOINTMENT' | 'NEW_REFERRAL' | 'WEEKLY_NEW_REFERRAL' | 'PATIENT_APPOINTMENT_REMINDER' | 'PATIENT_NEW_REFERRAL' | 'FOLLOWUP_ON_CLINLIFE_REGISTRATION' | 'FINAL_ATTEMPT_NOTICE' | 'RECONTACTED_PATIENT_NOTIFICATION' | 'PATIENT_CONTACT_CONFIRMATION' | 'POST_SCREEN_SURVEY' | 'LINK_TO_UPLOAD_EHR',
    ): CancelablePromise<any> {
        return __request({
            method: 'POST',
            path: `/referral/getEmailTemplate`,
            query: {
                'referralId': referralId,
                'templateKey': templateKey,
            },
        });
    }

    /**
     * Get SMS template
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static referralControllerGetSmsTemplate(
        requestBody: GetSmsTemplateDto,
    ): CancelablePromise<any> {
        return __request({
            method: 'POST',
            path: `/referral/getSmsTemplate`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Generate reschedule contact time url
     * @param referralId
     * @returns string
     * @throws ApiError
     */
    public static referralControllerGetRescheduleContactTimeUrl(
        referralId: string,
    ): CancelablePromise<string> {
        return __request({
            method: 'GET',
            path: `/referral/rescheduleContactTimeUrl/${referralId}`,
        });
    }

    /**
     * Update referral protocol
     * @param referralId
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static referralControllerUpdateCurrentUserProtocol(
        referralId: string,
        requestBody: ProtocolInfoDto,
    ): CancelablePromise<any> {
        return __request({
            method: 'POST',
            path: `/referral/update-protocol/${referralId}`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Get referral protocol
     * @param referralId
     * @returns ProtocolListDto
     * @throws ApiError
     */
    public static referralControllerGetCurrentUserProtocol(
        referralId: string,
    ): CancelablePromise<ProtocolListDto> {
        return __request({
            method: 'POST',
            path: `/referral/get-protocol/${referralId}`,
        });
    }

    /**
     * Check if referral is locked by other employee
     * @param referralId
     * @returns ReferralLockDto
     * @throws ApiError
     */
    public static referralControllerCheckReferralLock(
        referralId: string,
    ): CancelablePromise<ReferralLockDto> {
        return __request({
            method: 'GET',
            path: `/referral/checkReferralLock/${referralId}`,
        });
    }

    /**
     * release referral lock
     * @param referralId
     * @returns ReferralLockDto
     * @throws ApiError
     */
    public static referralControllerReleaseReferralLock(
        referralId: string,
    ): CancelablePromise<ReferralLockDto> {
        return __request({
            method: 'POST',
            path: `/referral/releaseReferralLock/${referralId}`,
        });
    }

    /**
     * add/update referral availability
     * @param requestBody
     * @returns ReferralInvestigatorDto
     * @throws ApiError
     */
    public static referralControllerAddReferralAvailability(
        requestBody: ReferralUpdateDto,
    ): CancelablePromise<ReferralInvestigatorDto> {
        return __request({
            method: 'POST',
            path: `/referral/addAvailability`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * add/update protocol 801 participation
     * @param requestBody
     * @returns Protocol801ParticipationUpdateDto
     * @throws ApiError
     */
    public static referralControllerAddProtocol801Participation(
        requestBody: Protocol801ParticipationUpdateDto,
    ): CancelablePromise<Protocol801ParticipationUpdateDto> {
        return __request({
            method: 'POST',
            path: `/referral/addProtocol801Participation`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * add/update sponsor patient ID
     * @param requestBody
     * @returns SponsorPatientIDDto
     * @throws ApiError
     */
    public static referralControllerAddSponsorPatientId(
        requestBody: SponsorPatientIDDto,
    ): CancelablePromise<SponsorPatientIDDto> {
        return __request({
            method: 'POST',
            path: `/referral/addSponsorPatientID`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * update 601 visit status
     * @param requestBody
     * @returns Visit601StatusDto
     * @throws ApiError
     */
    public static referralControllerUpdate601VisitStatus(
        requestBody: Visit601StatusDto,
    ): CancelablePromise<Visit601StatusDto> {
        return __request({
            method: 'POST',
            path: `/referral/updateVisit601Status`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Request medical records via Seqster
     * @param id
     * @param requestBody
     * @returns RequestMedicalRecordsResponseDto
     * @throws ApiError
     */
    public static referralControllerRequestMedicalRecords(
        id: string,
        requestBody: RequestMedicalRecordsDto,
    ): CancelablePromise<RequestMedicalRecordsResponseDto> {
        return __request({
            method: 'POST',
            path: `/referral/${id}/request-medical-records`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Create a one-time Seqster viewing link
     * @param id
     * @returns MedicalRecordsViewingLinkResponseDto
     * @throws ApiError
     */
    public static referralControllerGetMedicalRecordsViewingLink(
        id: string,
    ): CancelablePromise<MedicalRecordsViewingLinkResponseDto> {
        return __request({
            method: 'POST',
            path: `/referral/${id}/medical-records-viewing-link`,
        });
    }

    /**
     * Update referral recontact mark
     * @param referralId
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static referralControllerUpdateRecontactMark(
        referralId: string,
        requestBody: ReferralRecontactMarkDto,
    ): CancelablePromise<any> {
        return __request({
            method: 'POST',
            path: `/referral/recontact-mark/${referralId}`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Update healthcare provider details
     * @param id
     * @param requestBody
     * @returns HealthcareProviderDto
     * @throws ApiError
     */
    public static referralControllerUpdateHealthcareProvider(
        id: string,
        requestBody: UpdateHealthcareProviderDto,
    ): CancelablePromise<HealthcareProviderDto> {
        return __request({
            method: 'PATCH',
            path: `/referral/${id}/healthcare-provider`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Update referral phone number and preserve history
     * @param id
     * @param phoneNumber
     * @returns string
     * @throws ApiError
     */
    public static referralControllerPatchPhoneNumber(
        id: string,
        phoneNumber: string,
    ): CancelablePromise<string> {
        return __request({
            method: 'PATCH',
            path: `/referral/phone-number`,
            query: {
                'id': id,
                'phoneNumber': phoneNumber,
            },
        });
    }

    /**
     * @returns any
     * @throws ApiError
     */
    public static referralControllerUploadJson(): CancelablePromise<any> {
        return __request({
            method: 'POST',
            path: `/referral/mapYobAnswerToReferral`,
        });
    }

}